// Walking nested `{ id, children }` nodes - shared by the Tree component and the
// pages that drive it, which both need to resolve an id back to its node.
export interface TreeNode {
  id: string;
  label?: string;
  children?: TreeNode[];
  [key: string]: unknown;
}

export function findNode<T extends TreeNode>(nodes: T[], id: string | undefined): T | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode((node.children ?? []) as T[], id);
    if (found) return found;
  }
  return null;
}

// The ids of a node's ancestors, outermost first, or null when it is not there.
// An empty array means the node sits at the top level.
export function pathTo(
  nodes: TreeNode[],
  id: string | undefined,
  trail: string[] = [],
): string[] | null {
  for (const node of nodes) {
    if (node.id === id) return trail;
    const found = pathTo(node.children ?? [], id, [...trail, node.id]);
    if (found) return found;
  }
  return null;
}

export interface TreeSearchResult<T> {
  items: T[];
  expand: string[];
}

// Narrows a tree to what matches `needle`. A match keeps its subtree; ancestors
// stay reachable and land in `expand`. A match with no inner match stays closed.
export function searchTree<T extends TreeNode>(nodes: T[], needle: string): TreeSearchResult<T> {
  const items: T[] = [];
  const expand: string[] = [];
  for (const node of nodes) {
    const inChildren = searchTree((node.children ?? []) as T[], needle);
    const isMatch = !!node.label?.toLowerCase().includes(needle);
    if (!isMatch && !inChildren.items.length) continue;

    items.push(isMatch ? node : { ...node, children: inChildren.items });
    if (inChildren.items.length) expand.push(node.id, ...inChildren.expand);
  }
  return { items, expand };
}

// Every id in tree order. A key for "the tree's shape changed", as distinct from
// a node's own contents changing.
export function treeIds(nodes: TreeNode[]): string[] {
  return nodes.flatMap((node) => [node.id, ...treeIds(node.children ?? [])]);
}
